import { JarvisOnbaseDocument, OnBaseDocumentType, PdfReturnObject } from './AIMApi';
import { AriApiQueryCreatorFetch } from './AriApiCreator';
import { prismScopes } from './auth';
import getPrismBaseUrl, { prismFunctionCode } from './AIMApi/controllers/utils/getBaseUrl';
import type {
  JarvisPmiLease,
  JarvisPmiDeed,
  JarvisPmiEasement,
  JarvisPmiSolar,
  JarvisPmiPermit,
  JarvisPmiLicense,
  JarvisPmiPointOfDiversion,
  PmiLandActivityCount,
  JarvisWaterAsset,
  JarvisPmiEnvision,
  JarvisPmiMiscellaneous,
  JarvisPmiNonDeedInternalTransfer,
  JarvisPmiProfit,
  JarvisPmiRestriction,
  RenewablesSummary,
  RenewableLease,
  RenewableFinancials,
  LandAgreementLease,
  AgreementFinancials,
} from './JarvisApi/models';


const serviceurl = getPrismBaseUrl();

const JarvisApi = AriApiQueryCreatorFetch({
  serviceURL: serviceurl,
  scopesObj: prismScopes,
  functionCode: prismFunctionCode,
  apiOptions: {
    reducerPath: 'jarvisApi',
    endpoints: (builder) => ({
      documentsByFarmNumber: builder.query<JarvisOnbaseDocument[], string>({
        query: (farmNumber) => ({ url: '/GetDocumentsByFarmNumber', params: { farmNumber } })
      }),
      solarLeaseDocumentsBySmartId: builder.query<JarvisOnbaseDocument[], string>({
        query: (smartId) => ({ url: '/GetSolarLeaseDocumentsBySmartId', params: { smartId } })
      }),
      documentDetails: builder.query<PdfReturnObject, number>({
        query: (docHandle) => ({ url: '/GetDocument', params: { docHandle } })
      }),
      documentPdf: builder.query<PdfReturnObject, number>({
        query: (docHandle) => ({ url: '/GetDocumentPdf', params: { docHandle } })
      }),
      documentTypes: builder.query<OnBaseDocumentType[], null>({
        query: () => ({ url: '/DocumentTypes' })
      }),
      farm: builder.query<Record<string, unknown>, string>({
        query: (farmNumber) => ({ url: '/Farms', params: { FarmNumber: farmNumber } })
      }),
      waterAssets: builder.query<JarvisWaterAsset[], string>({
        query: (farmNumber) => ({ url: '/WaterAssets', params: { FarmNumber: farmNumber } })
      }),
      pmiDeeds: builder.query<JarvisPmiDeed[], string>({
        query: (farmNumber) => ({ url: '/PmiDeeds', params: { FarmNumber: farmNumber } })
      }),
      pmiEasements: builder.query<JarvisPmiEasement[], string>({
        query: (farmNumber) => ({ url: '/PmiEasements', params: { FarmNumber: farmNumber } })
      }),
      pmiLeases: builder.query<JarvisPmiLease[], string>({
        query: (farmNumber) => ({ url: '/PmiLeases', params: { FarmNumber: farmNumber } })
      }),
      pmiPermits: builder.query<JarvisPmiPermit[], string>({
        query: (farmNumber) => ({ url: '/PmiPermits', params: { FarmNumber: farmNumber } })
      }),
      pmiLicenses: builder.query<JarvisPmiLicense[], string>({
        query: (farmNumber) => ({ url: '/PmiLicenses', params: { FarmNumber: farmNumber } })
      }),
      pmiPointsOfDiversion: builder.query<JarvisPmiPointOfDiversion[], string>({
        query: (farmNumber) => ({ url: '/PmiPointOfDiversions', params: { FarmNumber: farmNumber } })
      }),
      pmiLandActivityCounts: builder.query<PmiLandActivityCount[], string>({
        query: (farmNumber) => ({ url: '/PmiLandActivityCounts', params: { FarmNumber: farmNumber } })
      }),
      pmiEnvisions: builder.query<JarvisPmiEnvision[], string>({
        query: (farmNumber) => ({ url: '/PmiEnvisions', params: { FarmNumber: farmNumber } })
      }),
      pmiMiscellaneous: builder.query<JarvisPmiMiscellaneous[], string>({
        query: (farmNumber) => ({ url: '/PmiMiscellaneous', params: { FarmNumber: farmNumber } })
      }),
      pmiNonDeedInternalTransfers: builder.query<JarvisPmiNonDeedInternalTransfer[], string>({
        query: (farmNumber) => ({ url: '/PmiNonDeedInternalTransfers', params: { FarmNumber: farmNumber } })
      }),
      pmiProfits: builder.query<JarvisPmiProfit[], string>({
        query: (farmNumber) => ({ url: '/PmiProfits', params: { FarmNumber: farmNumber } })
      }),
      pmiRestrictions: builder.query<JarvisPmiRestriction[], string>({
        query: (farmNumber) => ({ url: '/PmiRestrictions', params: { FarmNumber: farmNumber } })
      }),
      pmiSolarLeases: builder.query<JarvisPmiSolar[], string>({
        query: (farmNumber) => ({ url: '/PmiSolarLeasesByFarm', params: { FarmNumber: farmNumber } })
      }),
      solarLeasesAll: builder.query<JarvisPmiSolar[], null>({
        keepUnusedDataFor: 1200,
        query: () => ({ url: '/PmiSolarLeases' })
      }),
      renewablesByFarm: builder.query<RenewablesSummary, string>({
        query: (farmNumber) => ({ url: '/RenewablesByFarm', params: { FarmNumber: farmNumber } })
      }),
      renewablesByFarms: builder.mutation<RenewableLease[], string[]>({
        query: (farmNumbers) => ({ url: '/RenewablesByFarms', method: 'POST', body: farmNumbers })
      }),
      renewableFinancials: builder.query<RenewableFinancials, number>({
        query: (propertyItemId) => ({ url: '/RenewableFinancials', params: { PropertyItemId: propertyItemId } })
      }),
      renewablesBySmartIds: builder.mutation<RenewablesSummary, string[]>({
        query: (smartIds) => ({ url: '/RenewablesBySmartIds', method: 'POST', body: smartIds })
      }),
      activeLeasesByFarm: builder.query<LandAgreementLease[], string>({
        query: (farmNumber) => ({ url: '/ActiveLeasesByFarm', params: { FarmNumber: farmNumber } })
      }),
      agreementFinancials: builder.query<AgreementFinancials, number>({
        query: (agreementId) => ({ url: '/AgreementFinancials', params: { AgreementId: agreementId } })
      }),
    })
  }
});

/**
 * MSAL scope configuration for the Jarvis API, defining a user-impersonation scope for the AG Reserves domain.
 */
export const jarvisScopes = {
  scopes: ['https://agreserves.com/dd06119e-0342-4969-b7f2-5af5039c3d66/user_impersonation']
};

/**
 * Named export of the Jarvis RTK Query API service, exposing hooks for document retrieval, farm data, PMI records, renewables, and lease/financial APIs.
 */
export const jarvisService = JarvisApi;
export const {
  useLazyDocumentsByFarmNumberQuery,
  useLazySolarLeaseDocumentsBySmartIdQuery,
  useLazyDocumentPdfQuery,
  useDocumentTypesQuery,
  useLazyFarmQuery,
  useLazyWaterAssetsQuery,
  useWaterAssetsQuery,
  useLazyPmiDeedsQuery,
  usePmiDeedsQuery,
  useLazyPmiEasementsQuery,
  usePmiEasementsQuery,
  useLazyPmiLeasesQuery,
  usePmiLeasesQuery,
  useLazyPmiPermitsQuery,
  usePmiPermitsQuery,
  useLazyPmiLicensesQuery,
  usePmiLicensesQuery,
  useLazyPmiPointsOfDiversionQuery,
  usePmiPointsOfDiversionQuery,
  useLazyPmiLandActivityCountsQuery,
  usePmiLandActivityCountsQuery,
  usePmiEnvisionsQuery,
  useLazyPmiEnvisionsQuery,
  usePmiMiscellaneousQuery,
  useLazyPmiMiscellaneousQuery,
  usePmiNonDeedInternalTransfersQuery,
  useLazyPmiNonDeedInternalTransfersQuery,
  usePmiProfitsQuery,
  useLazyPmiProfitsQuery,
  usePmiRestrictionsQuery,
  useLazyPmiRestrictionsQuery,
  useLazyPmiSolarLeasesQuery,
  usePmiSolarLeasesQuery,
  useSolarLeasesAllQuery,
  useRenewablesByFarmQuery,
  useLazyRenewablesByFarmQuery,
  useRenewablesByFarmsMutation,
  useRenewableFinancialsQuery,
  useLazyRenewableFinancialsQuery,
  useRenewablesBySmartIdsMutation,
  useActiveLeasesByFarmQuery,
  useLazyActiveLeasesByFarmQuery,
  useAgreementFinancialsQuery,
  useLazyAgreementFinancialsQuery,
} = jarvisService.hooks;

export type {
  JarvisPmiLease,
  JarvisPmiDeed,
  JarvisPmiEasement,
  JarvisPmiSolar,
  JarvisPmiPermit,
  JarvisPmiLicense,
  JarvisPmiPointOfDiversion,
  PmiLandActivityCount,
  LandActivityType,
  JarvisWaterAsset,
  JarvisPmiEnvision,
  JarvisPmiMiscellaneous,
  JarvisPmiNonDeedInternalTransfer,
  JarvisPmiProfit,
  JarvisPmiRestriction,
  RenewablesSummary,
  RenewableLease,
  RenewableFinancials,
  PotentialRenewable,
  OptionFinancialRow,
  OptionFinancialSupplemental,
  OptionFinancialsDetail,
  LeaseFinancialRow,
  RenewablePayment,
  LandAgreementLease,
  AgreementFinancials,
  AgreementRentLineRow,
  AgreementBillingScheduleRow,
  AgreementYieldRow,
} from './JarvisApi/models';

